import SelectBeneficiaryModal from 'components/beneficiaries/sponsorships/sponsorship-form/SelectBeneficiaryModal';
import useSessionState from 'hooks/useSessionState';
import Avatar from 'components/common/Avatar';
import ExportModal from 'components/common/ExportModal';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import SoftBadge from 'components/common/SoftBadge';
import StatusBadge from './StatusBadge';
import TableRowAction from 'components/common/TableRowAction';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import DonorSponsorshipModal from 'components/donor-details/modals/DonorSponsorshipModal';
import DonorCounter from 'components/donor/DonorCounter';
import BeneficiariesFilterForm from './BeneficiariesFilterForm';
import { weBeneficiariesItems } from 'data/weraise-data/donor-data';
import { calculateAge, parseRouteParams } from 'helpers/utils';
import { format, addMonths } from 'date-fns';
import { useMemo, useState } from 'react';
import { Button, Col, Row, Card } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import paths from 'routes/paths';
import { Can } from 'routes/permissions/Can';
import {
  permissionsKeyMap,
  userActions
} from 'routes/permissions/permissionKeys';
import { useBeneficiaries, useDeleteBeneficiary } from 'hooks/useBeneficiaries';
import { useCurrentJuzByOrphan } from 'hooks/useHafizProgress';
import useBranchPermission from 'hooks/useBranchPermission';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { getColor } from 'helpers/utils';
import * as echarts from 'echarts/core';
import { PieChart, BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

// Register chart components
echarts.use([
  PieChart,
  BarChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  CanvasRenderer
]);

const AllBeneficiaries = () => {
  const navigate = useNavigate();

  const [modalShow, setModalShow] = useState(false);
  const [beneficiaryModalShow, setBeneficiaryModalShow] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [filters, setFilters] = useState({});
  // What was typed into the filter bar, kept for this tab so coming back to
  // the list (e.g. from a child's profile) shows the same filtered list
  const [savedFilterForm, setSavedFilterForm] = useSessionState(
    'filters:beneficiaries',
    null
  );

  const { beneficiaries, isLoading: loading } = useBeneficiaries();
  const { currentJuzByOrphan } = useCurrentJuzByOrphan();
  const { deleteBeneficiaryAsync, isDeleting: deleteLoading } =
    useDeleteBeneficiary();
  const { isMainBranch } = useBranchPermission();

  // Extract unique countries from beneficiaries
  const countries = useMemo(() => {
    if (!beneficiaries) return [];
    const uniqueCountries = [
      ...new Set(beneficiaries.map(b => b.country).filter(Boolean))
    ];
    return uniqueCountries.sort();
  }, [beneficiaries]);

  // Extract unique genders
  const genders = useMemo(() => {
    if (!beneficiaries) return [];
    const uniqueGenders = [
      ...new Set(beneficiaries.map(b => b.gender).filter(Boolean))
    ];
    return uniqueGenders.sort();
  }, [beneficiaries]);
  const tableData = useMemo(
    () =>
      beneficiaries?.map(
        ({
          id,
          simple_id: simpleId,
          first_name,
          middle_name,
          last_name,
          hy_connect_id,
          DOB,
          country,
          branch,
          sponsorship_priority,
          sponsorship_type,
          sponsorship_status,
          gender,
          duration,
          sponsorships,
          beneficiary_sponsorship_types,
          enrollment_date,
          left_programme_at,
          created_at,
          school_students,
          mosque_orphan,
          hospital_orphan,
          orphan_orphanage
        }) => {
          // Current (open) facility enrolments, for the School / Facility filter
          const open = rows => (rows || []).filter(r => !r.end_date);
          const facilities = [
            ...open(school_students).map(r => r.school?.school_name),
            ...open(mosque_orphan).map(r => r.mosque?.name),
            ...open(hospital_orphan).map(r => r.hospital?.name),
            ...open(orphan_orphanage).map(r => r.orphanage?.name)
          ].filter(Boolean);
          // Calculate sponsored until date from the latest ACTIVE sponsorship
          let sp_validity = null;
          const activeSponsorships =
            sponsorships?.filter(s => s.status === 'active') || [];
          if (activeSponsorships.length > 0) {
            // Get the most recent active sponsorship
            const latestSponsorship = activeSponsorships.sort(
              (a, b) => new Date(b.start_date) - new Date(a.start_date)
            )[0];
            if (latestSponsorship.start_date && latestSponsorship.duration) {
              const endDate = addMonths(
                new Date(latestSponsorship.start_date),
                latestSponsorship.duration
              );
              sp_validity = format(endDate, 'dd/MM/yyyy');
            }
          }

          // Get all sponsorship types from junction table, fallback to legacy FK
          const types =
            beneficiary_sponsorship_types?.length > 0
              ? beneficiary_sponsorship_types
                  .map(bst => bst.sponsorship_type?.type)
                  .filter(Boolean)
                  .join(', ')
              : sponsorship_type?.type;

          return {
            id,
            simpleId,
            hyConnectId: hy_connect_id || '',
            // Collapse whitespace: imported names carry trailing spaces, which
            // made "Ali Husein Ali" unsearchable ("Ali  Husein Ali" in data)
            name: [first_name, middle_name, last_name]
              .filter(Boolean)
              .join(' ')
              .replace(/\s+/g, ' ')
              .trim(),
            age: calculateAge(DOB),
            country,
            branchName: branch?.name,
            sponsor: '-',
            sp_validity,
            priority: sponsorship_priority?.priority_type,
            type: types,
            gender,
            status: sponsorship_status?.status,
            statusColor: sponsorship_status?.color,
            statusHidden: Boolean(sponsorship_status?.is_hidden),
            isHafiz: /hafiz|hifz/i.test(types || ''),
            enrollmentDate: enrollment_date || null,
            leftProgrammeAt: left_programme_at || null,
            // Timestamp -> the local day it was created, for the date filter
            createdAt: created_at
              ? format(new Date(created_at), 'yyyy-MM-dd')
              : null,
            facilities,
            currentJuz: currentJuzByOrphan[id]?.juz ?? null,
            currentJuzDate: currentJuzByOrphan[id]?.recorded_on ?? null
          };
        }
      ),
    [beneficiaries, currentJuzByOrphan]
  );

  // Priority order for sorting: Critical (highest), High, Normal, Special Case (lowest)
  const priorityOrder = {
    Critical: 4,
    High: 3,
    Normal: 2,
    'Special Case': 1
  };

  // Filter and sort tableData based on selected filters and priority.
  // Each filter is a multi-select: OR within a filter, AND across filters.
  const filteredTableData = useMemo(() => {
    if (!tableData) return [];
    const active = key => filters[key]?.length > 0;
    const filtered = tableData.filter(row => {
      // Grey statuses (left the programme) stay out of the list unless the
      // status filter explicitly asks for them - or a left-programme date
      // range is being searched
      const leftDateSearch = Boolean(filters.leftFrom || filters.leftTo);
      if (
        row.statusHidden &&
        !filters.status?.includes(row.status) &&
        !leftDateSearch
      )
        return false;
      if (leftDateSearch) {
        if (!row.leftProgrammeAt) return false;
        if (filters.leftFrom && row.leftProgrammeAt < filters.leftFrom)
          return false;
        if (filters.leftTo && row.leftProgrammeAt > filters.leftTo)
          return false;
      }
      if (active('country') && !filters.country.includes(row.country))
        return false;
      if (active('gender') && !filters.gender.includes(row.gender))
        return false;
      if (active('priority') && !filters.priority.includes(row.priority))
        return false;
      // row.type is a comma-joined list of the child's types
      if (active('type') && !filters.type.some(t => row.type?.includes(t)))
        return false;
      if (active('status') && !filters.status.includes(row.status))
        return false;
      if (
        active('juz') &&
        !filters.juz.map(Number).includes(Number(row.currentJuz))
      )
        return false;
      if (
        filters.name &&
        !row.name.toLowerCase().includes(filters.name.toLowerCase())
      )
        return false;
      if (active('age')) {
        const age = Number(row.age);
        const inRange = filters.age.some(range => {
          const [lo, hi] = range.split('-').map(Number);
          return Number.isFinite(age) && age >= lo && age <= hi;
        });
        if (!inRange) return false;
      }
      if (
        active('facility') &&
        !row.facilities.some(f => filters.facility.includes(f))
      )
        return false;
      if (filters.enrolledFrom || filters.enrolledTo) {
        if (!row.enrollmentDate) return false;
        if (filters.enrolledFrom && row.enrollmentDate < filters.enrolledFrom)
          return false;
        if (filters.enrolledTo && row.enrollmentDate > filters.enrolledTo)
          return false;
      }
      if (filters.createdFrom || filters.createdTo) {
        if (!row.createdAt) return false;
        if (filters.createdFrom && row.createdAt < filters.createdFrom)
          return false;
        if (filters.createdTo && row.createdAt > filters.createdTo)
          return false;
      }
      return true;
    });

    // Sort by priority order: Critical, High, Normal, Special Case (Critical first)
    return filtered.sort((a, b) => {
      const priorityA = priorityOrder[a.priority] ?? 999;
      const priorityB = priorityOrder[b.priority] ?? 999;
      return priorityB - priorityA;
    });
  }, [tableData, filters]);

  // Handle filter form submission
  const handleFilter = data => {
    setSavedFilterForm(data);
    setFilters({
      country: data.country || [],
      gender: data.gender || [],
      priority: data.priority || [],
      type: data.type || [],
      status: data.status || [],
      juz: data.juz || [],
      name: (data.name || '').trim(),
      age: data.age || [],
      facility: data.facility || [],
      enrolledFrom: data.enrolled?.from || '',
      enrolledTo: data.enrolled?.to || '',
      leftFrom: data.left?.from || '',
      leftTo: data.left?.to || '',
      createdFrom: data.created?.from || '',
      createdTo: data.created?.to || ''
    });
  };

  // Facility names across all children, for the filter options
  const facilities = useMemo(
    () =>
      [...new Set((tableData || []).flatMap(r => r.facilities || []))].sort(),
    [tableData]
  );

  const columns = [
    {
      accessor: 'id',
      Header: 'ID',
      cellProps: {
        className: 'py-2 fw-medium '
      },
      Cell: rowData => {
        const { id, simpleId } = rowData.row.original;
        return (
          <Link
            to={`/beneficiaries/${id}/profile`}
            className="fw-medium text-1100 hover-primary"
          >
            {`B${simpleId}`}
          </Link>
        );
      }
    },
    {
      accessor: 'hyConnectId',
      Header: 'HY Connect ID',
      cellProps: {
        className: 'py-2 fw-medium '
      },
      Cell: rowData => {
        const { hyConnectId } = rowData.row.original;
        return hyConnectId ? hyConnectId : <>&mdash;</>;
      }
    },
    {
      accessor: 'name',
      Header: 'Name',
      cellProps: {
        className: 'py-2 fw-medium '
      },
      Cell: rowData => {
        const { name, image, id } = rowData.row.original;
        return (
          <Flex alignItems="center">
            <Avatar src={image} rounded="circle" size="xl" name={name} />
            <Button
              variant="link"
              onClick={() =>
                navigate(
                  parseRouteParams(paths.beneficiaryProfile, {
                    id
                  })
                )
              }
              className="fw-medium ms-2 fs--1 p-0 text-1100 hover-primary"
            >
              {name}
            </Button>
          </Flex>
        );
      }
    },
    {
      accessor: 'age',
      Header: 'Age',
      cellProps: {
        className: 'py-2 fw-medium '
      }
    },
    {
      accessor: 'country',
      Header: 'Location',
      cellProps: {
        className: 'py-2 fw-medium '
      }
    },
    // HQ views across branches, so show which branch each child belongs to
    ...(isMainBranch
      ? [
          {
            accessor: 'branchName',
            Header: 'Branch',
            cellProps: {
              className: 'py-2 fw-medium '
            },
            Cell: ({ value }) => value || '-'
          }
        ]
      : []),
    {
      accessor: 'gender',
      Header: 'Gender',
      cellProps: {
        className: 'py-2 fw-medium '
      }
    },
    // {
    //   accessor: 'sponsor',
    //   DonationHeader: 'Sponsor',
    //   cellProps: {
    //     className: 'fw-medium text-800 py-2'
    //   }
    // },
    {
      accessor: 'sp_validity',
      Header: 'Sp. Until',
      cellProps: {
        className: 'fw-medium py-2'
      },
      Cell: rowData => {
        const { sp_validity } = rowData.row.original;
        return sp_validity ? sp_validity : <>&mdash;</>;
      }
    },

    {
      accessor: 'priority',
      Header: 'Priority',
      cellProps: {
        className: 'fw-medium  py-2'
      },
      Cell: rowData => {
        const { priority } = rowData.row.original;
        if (!priority) return <>&mdash;</>;

        // Assign colors based on priority keywords
        const priorityLower = priority.toLowerCase();
        let bgColor = 'secondary';
        if (
          priorityLower.includes('urgent') ||
          priorityLower.includes('critical')
        ) {
          bgColor = 'danger';
        } else if (priorityLower.includes('high')) {
          bgColor = 'warning';
        } else if (
          priorityLower.includes('medium') ||
          priorityLower.includes('normal')
        ) {
          bgColor = 'info';
        } else if (priorityLower.includes('low')) {
          bgColor = 'success';
        }

        return (
          <SoftBadge pill className="me-2" bg={bgColor}>
            {priority}
          </SoftBadge>
        );
      }
    },
    {
      accessor: 'type',
      Header: 'Type',
      cellProps: {
        className: 'fw-medium text-800 py-2'
      }
    },
    {
      accessor: 'currentJuz',
      Header: 'Juz',
      cellProps: { className: 'py-2' },
      Cell: rowData => {
        const { isHafiz, currentJuz, currentJuzDate } = rowData.row.original;
        if (!isHafiz) return <span className="text-400">&mdash;</span>;
        if (!currentJuz)
          return <span className="text-500 fs--1">Not recorded</span>;
        return (
          <span title={currentJuzDate ? `Since ${currentJuzDate}` : undefined}>
            <SoftBadge bg="primary" pill>
              Juz {currentJuz}
            </SoftBadge>
          </span>
        );
      }
    },
    {
      accessor: 'status',
      Header: 'Status',
      cellProps: {
        className: 'fw-medium text-800 py-2'
      },
      Cell: rowData => {
        const { status, statusColor, statusHidden } = rowData.row.original;
        return (
          <StatusBadge
            status={status}
            color={statusColor}
            hidden={statusHidden}
          />
        );
      }
    },
    {
      accessor: 'id_d',
      Header: '',
      headerProps: {
        className: 'text-end'
      },
      cellProps: {
        className: 'text-end fw-medium py-2'
      },
      disableSortBy: true,
      Cell: rowData => {
        const { id } = rowData.row;
        return (
          <TableRowAction
            onCLickEdit={() =>
              navigate(parseRouteParams(paths.editBeneficiary, { id }))
            }
            onCLickDelete={() => handleDeleteClick(id)}
            subject={permissionsKeyMap.sponsorship.children.beneficiaries}
          />
        );
      }
    }
  ];
  const handleDeleteClick = id => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };
  const handleDeleteModalHide = () => {
    setSelectedId(null);
    setShowDeleteModal(false);
  };
  const handleConfirmDelete = async () => {
    try {
      await deleteBeneficiaryAsync(selectedId);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting beneficiary:', error);
    }
  };
  const content = (
    <Can
      I={userActions.create}
      a={permissionsKeyMap.sponsorship.children.sponsorships}
    >
      <IconButton
        variant="falcon-default"
        size="sm"
        icon="link"
        transform="shrink-3"
        className="me-2"
        onClick={() => setBeneficiaryModalShow(true)}
        style={{ fontSize: '1rem' }}
      >
        <span className="d-none d-lg-inline-block ms-2">Sponsor</span>
      </IconButton>
    </Can>
  );
  const hideSponsorshipModal = () => {
    setBeneficiaryModalShow(false);
  };
  const handleSelectBeneficiarySubmit = beneficiaryId => {
    navigate(
      parseRouteParams(
        paths.createNewSponsorship,
        {},
        {
          beneficiaryId: beneficiaryId
        }
      )
    );
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  return (
    <>
      <ConfirmationModal
        show={showDeleteModal}
        onHide={handleDeleteModalHide}
        loading={deleteLoading}
        title="Delete Package"
        handleConfirm={handleConfirmDelete}
        body="Are you sure you want to delete selected beneficiary?"
      />
      <SelectBeneficiaryModal
        onHide={hideSponsorshipModal}
        show={beneficiaryModalShow}
        onSubmit={handleSelectBeneficiarySubmit}
      />

      <Row className="opacity-25 d-none">
        <Col>
          <DonorCounter
            counterData={weBeneficiariesItems({
              totalBeneficiaries: tableData?.length ?? 0
            })}
          />
        </Col>
      </Row>
      {/* Filter Card */}
      <Row className="mb-3">
        <Col>
          <Card className="shadow-none border">
            <Card.Body>
              <BeneficiariesFilterForm
                onFilter={handleFilter}
                initialValues={savedFilterForm}
                countries={countries}
                genders={genders}
                facilities={facilities}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12} xxl={12} className="h-md-100 ">
          {tableData && (
            <CustomAdvanceTable
              data={filteredTableData}
              columns={columns}
              title={`Beneficiaries (${filteredTableData?.length ?? 0})`}
              headerContent={content}
              addButtonLabel="Add Beneficiary"
              onClickAddNew={() => navigate(paths.addBeneficiary)}
              addNew
              addButtonIcon="child"
              showSearchInput
              searchInputPlaceHolder="Search Beneficiaries"
              perPage={50}
              loading={loading}
              showTablePagination
              hasExport
              handleExport={handleExport}
              subject={permissionsKeyMap.sponsorship.children.beneficiaries}
            />
          )}
        </Col>
      </Row>
      {modalShow && (
        <DonorSponsorshipModal
          setModalShow={setModalShow}
          modalShow={modalShow}
        />
      )}
      <ExportModal
        data={tableData ?? []}
        columns={columns.slice(0, columns.length - 1)}
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        fileName={'User List'}
      />
    </>
  );
};

export default AllBeneficiaries;
